import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Avatar, Box, Button, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import { refreshMyLabs } from 'shared/hooks/useMyLabs';
import {
  Add as AddIcon, Business as BusinessIcon, CalendarMonth as LabCalendarIcon, EventAvailable as MeetingIcon, Chat as ChatIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, Groups as GroupsIcon, Lock as LockIcon, Public as PublicIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon, Language as LanguageIcon, Logout as LeaveIcon,
  School as SchoolIcon, WorkOutline as WorkIcon, HistoryEdu as LetterIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { useUserContext } from 'shared/contexts/UserContext';
import BackButton from 'components/common/buttons/BackButton';
import FollowButton from 'components/common/buttons/FollowButton';
import { ConfirmIconDelete, ItemDialog, LabEditDialog, MemberDialog } from './LabDialogs';
import { ApplicantsDialog, ApplyDialog, LabCallsBody, LabChatDrawer, LabFollowersBody, LabStatsBody } from './LabExtras';
import { coverBackground, labAvatarSx, labColor, tint } from './labTheme';
import otableLogo from 'media/otable_main.png';
import LabMarkdown from './LabMarkdown';
import LabNewsCarousel from './LabNewsCarousel';
import { LabConsortiaBody, LabWorksBody } from './LabWork';
import ReferenceLetterDialog from './ReferenceLetterDialog';
import { useDocumentTitle } from 'shared/hooks/useDocumentTitle';
import MeetingScheduleDialog from 'components/common/dialogs/MeetingScheduleDialog';

// "Microbial Ecology Lab" -> "MEL": the lab's own avatar where a news item names nobody
const labInitials = (name = '') => name.split(/\s+/).filter((w) => /^[\p{L}\d]/u.test(w)).slice(0, 3).map((w) => w[0]).join('').toUpperCase() || 'L';
const PUBLICATIONS_SHOWN = 5;
// The public page is read by visitors, not users at work: larger, comfortable text sizes
const PUBLIC_TYPE_SCALE = {          // px: the app's root font size is 10px, so rem would stay tiny
  '& .MuiTypography-body1': { fontSize: '17px', lineHeight: 1.7 },
  '& .MuiTypography-body2': { fontSize: '15.5px', lineHeight: 1.6 },
  '& .MuiTypography-caption': { fontSize: '13.5px', lineHeight: 1.5 },
  '& .MuiTypography-overline': { fontSize: '12px' },
  '& .MuiTypography-h6': { fontSize: '19px' },
  '& .MuiPaper-root .MuiChip-label': { fontSize: '13px' },
  '& .MuiButton-root': { fontSize: '13.5px' },
  '& .MuiAvatar-root': { fontSize: '15px' },
  '& li::marker': { fontSize: '13px' },
};

const KIND_LABEL = { phd: 'PhD', postdoc: 'Postdoc', masters: "Master's", ra: 'Research assistant', technician: 'Technician', intern: 'Intern', scientist: 'Scientist', engineer: 'Engineer', faculty: 'Faculty', manager: 'Project / lab manager', other: 'Other' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '');
const fmtMonth = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : '');

// In the public-page preview each section shows whether visitors see it; the PI switches it on/off there
const PreviewContext = createContext(null);

function PublicToggle({ pubKey }) {
  const ctx = useContext(PreviewContext);
  if (!ctx || !pubKey) return null;
  const on = ctx.publicSet.has(pubKey);
  const label = on ? 'Shown publicly' : 'Hidden from visitors';
  if (!ctx.canToggle) return <Chip size="small" label={label} sx={{ height: 22, ml: 1 }} />;
  return (
    <Tooltip title={on ? 'Click to hide this section from the public page' : 'Click to show this section on the public page'}>
      <Chip size="small" clickable onClick={() => ctx.toggle(pubKey)} disabled={ctx.saving}
        icon={on ? <VisibilityIcon /> : <VisibilityOffIcon />} label={label}
        color={on ? 'success' : 'default'} variant={on ? 'filled' : 'outlined'} sx={{ height: 24, ml: 1 }} />
    </Tooltip>
  );
}

function Section({ title, count, onAdd, addLabel, color, children, empty, pubKey }) {
  const ctx = useContext(PreviewContext);
  const hidden = ctx && pubKey && !ctx.publicSet.has(pubKey);
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2, transition: 'opacity 0.2s',
      ...(hidden && { opacity: 0.45, borderStyle: 'dashed' }) }}>
      <Box display="flex" alignItems="center" mb={1.5}>
        <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: color, mr: 1.25 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          {title}{count ? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>{count}</Box> : null}
        </Typography>
        <Box flex={1} />
        <PublicToggle pubKey={pubKey} />
        {onAdd && <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>{addLabel || 'Add'}</Button>}
      </Box>
      {children || <Typography variant="body2" color="text.secondary">{empty}</Typography>}
    </Paper>
  );
}

function ProjectRow({ p, color, controls, muted }) {
  return (
    <Box display="flex" alignItems="flex-start" gap={1} py={0.75} sx={{ opacity: muted ? 0.85 : 1 }}>
      <Box flex={1}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {p.acronym && <Box component="span" sx={{ color, mr: 0.75 }}>{p.acronym}</Box>}
          {p.url ? <Link href={p.url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">{p.title}</Link> : p.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {[p.funder, p.grant_number && `#${p.grant_number}`, p.role, (p.start_date || p.end_date) && `${fmtMonth(p.start_date)} – ${p.end_date ? fmtMonth(p.end_date) : 'ongoing'}`].filter(Boolean).join(' · ')}
        </Typography>
        {p.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{p.description}</Typography>}
      </Box>
      {p.source === 'profile'
        ? <Tooltip title="From the PI's profile; edit it there"><Chip label="PI profile" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', color: 'text.secondary' }} /></Tooltip>
        : controls}
    </Box>
  );
}

function PersonRow({ m, lab, canEdit, onEdit, onRemove, alumni, noLinks, onLetter }) {
  const navigate = useNavigate();
  const isPI = String(m.user) === String(lab.pi);
  const go = m.person && !noLinks ? () => navigate(`/profile/${m.person.id}`) : undefined;
  return (
    <Box display="flex" alignItems="center" gap={1.25} py={0.75}>
      <Avatar src={m.person?.avatar_url || undefined} onClick={go}
        sx={{ width: alumni ? 32 : 40, height: alumni ? 32 : 40, cursor: go ? 'pointer' : 'default', fontSize: '0.9rem', ...labAvatarSx(lab) }}>
        {(m.display_name || '?')[0]}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" noWrap onClick={go}
          sx={{ fontWeight: 600, cursor: go ? 'pointer' : 'default', '&:hover': go ? { textDecoration: 'underline' } : undefined }}>
          {m.display_name}
          {isPI && <Chip label="PI" size="small" sx={{ ml: 0.75, height: 18, fontSize: '0.65rem', bgcolor: labColor(lab), color: '#fff' }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {alumni
            ? [m.lab_title, (m.start_year || m.end_year) && `${m.start_year || ''}–${m.end_year || ''}`, m.current_position && `now ${m.current_position}`].filter(Boolean).join(' · ')
            : m.lab_title || m.person?.headline}
        </Typography>
      </Box>
      {/* {onLetter && !isPI && (
        <Tooltip title="Reference letter">
          <IconButton size="small" onClick={() => onLetter(m)} aria-label={`Reference letter for ${m.display_name}`}><LetterIcon sx={{ fontSize: 17 }} /></IconButton>
        </Tooltip>
      )} */}
      {canEdit && (
        <>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(m)} aria-label={`Edit ${m.display_name}`}><EditIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
          {!isPI && <ConfirmIconDelete label={`Remove ${m.display_name}`} onConfirm={() => onRemove(m)} />}
        </>
      )}
    </Box>
  );
}

/** The PI's lab meeting: the meeting scheduler with its poll, every current member on Otable invited. The list is
 *  built once per change of members (the scheduler starts over whenever it gets a new one). */
function LabMeetingDialog({ lab, open, onClose, onDone }) {
  const members = useMemo(() => (lab.members || [])
    .filter((m) => m.user && String(m.user) !== String(lab.pi))
    .map((m) => ({ id: m.user, name: m.person?.name || m.display_name, avatar_url: m.person?.avatar_url || null })),
  [lab.members, lab.pi]);
  return (
    <MeetingScheduleDialog open={open} onClose={onClose} requirePoll defaultTitle={`${lab.name} group meeting`}
      restrictToUsers={members} onDone={onDone} />
  );
}

export default function LabPage({ publicMode = false, preview = false }) {
  const { labId: labIdParam, labKey } = useParams();
  const labId = labIdParam || labKey;
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user } = useUserContext();
  const { data: lab, error, mutate } = useSWR(
    labId ? (preview ? `/consortium/labs/${labId}/public-preview/`
      : publicMode ? `/consortium/public/labs/${labId}/` : `/consortium/labs/${labId}/`) : null, fetcher);
  // The lab's consortia and works, already filtered for the viewer (PI: all, member: own, others: public consortia)
  const { data: work } = useSWR(lab?.id && !publicMode ? `/consortium/labs/${lab.id}/work/` : null, fetcher,
    { revalidateOnFocus: false });
  const [savingSections, setSavingSections] = useState(false);
  // Leaving is asked first: the PI has to invite you again to come back
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [allPublications, setAllPublications] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [memberDialog, setMemberDialog] = useState(null);   // {member?, alumni?}
  const [itemDialog, setItemDialog] = useState(null);       // {kind, item?}
  const [applyFor, setApplyFor] = useState(null);           // position
  const [applicantsFor, setApplicantsFor] = useState(null); // position
  const [chatOpen, setChatOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [letterFor, setLetterFor] = useState(null);         // member: the PI's reference letters
  // From a notification: ?panel=chat opens the team chat, ?applicants=<position> its applicants
  const { search } = useLocation();
  useEffect(() => {
    if (!lab) return;
    const q = new URLSearchParams(search);
    if (q.get('panel') === 'chat' && (lab.is_member || lab.can_edit)) setChatOpen(true);
    const pos = q.get('applicants') && lab.positions.find((p) => p.id === q.get('applicants'));
    if (pos && lab.can_edit) setApplicantsFor(pos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, lab?.id]);

  useDocumentTitle(lab?.name
    ? (publicMode ? `${lab.name} · ${lab.organization_detail?.name || ''}` : `${lab.name} | Otable`)
    : null);

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity={error.response?.status === 404 ? 'info' : 'error'}>
          {error.response?.status === 404
            ? (publicMode ? 'This lab page is not public.' : 'This lab page doesn’t exist or is visible to its members only.')
            : apiErrorMessage(error, 'Could not load the lab.')}
        </Alert>
      </Container>
    );
  }
  if (!lab) return <Box display="grid" sx={{ placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  const color = labColor(lab);
  const canEdit = lab.can_edit;
  const isPI = !publicMode && String(user?.id) === String(lab.pi);
  const ALL_SECTIONS = ['about', 'members', 'news', 'projects', 'publications', 'theses', 'positions', 'alumni'];
  const shown = new Set(publicMode && !preview ? lab.sections || [] : ALL_SECTIONS);
  const previewCtx = preview ? {
    publicSet: new Set(lab.sections || []),
    canToggle: String(user?.id) === String(lab.pi) || user?.is_staff,
    saving: savingSections,
    toggle: async (key) => {
      const cur = new Set(lab.sections || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      const next = ALL_SECTIONS.filter((k) => cur.has(k));
      setSavingSections(true);
      mutate({ ...lab, sections: next }, { revalidate: false });
      try {
        await axiosService.patch(`/consortium/labs/${lab.id}/`, { public_sections: next });
      } catch (err) {
        showSnackbar(apiErrorMessage(err, 'Could not change the public page.'), 'error');
      } finally {
        setSavingSections(false);
        mutate();
      }
    },
  } : null;
  const publicUrl = `${window.location.origin}/labs/${lab.slug || lab.id}/`;
  const saved = (data) => mutate(data, { revalidate: false });
  const removeMember = async (m) => {
    try {
      const res = await axiosService.delete(`/consortium/labs/${lab.id}/members/${m.id}/`);
      if (res.status === 204) { navigate('/hub/', { state: { tab: 2 } }); return; }   // left a members-only lab
      saved(res.data);
    } catch (err) { showSnackbar(apiErrorMessage(err), 'error'); }
  };
  const removeItem = async (kind, item) => {
    try { saved((await axiosService.delete(`/consortium/labs/${lab.id}/items/${kind}/${item.id}/`)).data); } catch (err) { showSnackbar(apiErrorMessage(err), 'error'); }
  };
  const leave = async () => {
    const me = lab.members.find((m) => String(m.user) === String(user?.id));
    if (!me) return;
    setLeaving(true);
    await removeMember(me);
    setLeaving(false);
    setConfirmLeave(false);
    refreshMyLabs();                                   // the lab leaves the sidebar
    showSnackbar(`You left ${lab.name}`, 'success');
  };
  const myMembership = lab?.members?.find((m) => String(m.user) === String(user?.id));
  // Members (and the PI) add theses and publications; the PI confirms members' entries
  const canContribute = !publicMode && (isPI || lab.is_member);
  const pendingOf = (kind) => (publicMode ? [] : (lab.pending_contributions || []).filter((c) => c.kind === kind));
  const reviewContribution = async (c, decision) => {
    try { saved((await axiosService.post(`/consortium/labs/${lab.id}/contributions/${c.kind}/${c.id}/review/`, { decision })).data); }
    catch (err) { showSnackbar(apiErrorMessage(err), 'error'); }
  };
  const removeContribution = async (kind, c) => {
    try { saved((await axiosService.delete(`/consortium/labs/${lab.id}/contributions/${kind}/${c.id}/`)).data); }
    catch (err) { showSnackbar(apiErrorMessage(err), 'error'); }
  };
  const setPublicationHidden = async (p, hidden) => {
    try { saved((await axiosService.post(`/consortium/labs/${lab.id}/publications/visibility/`, { id: p.id, hidden })).data); }
    catch (err) { showSnackbar(apiErrorMessage(err), 'error'); }
  };
  const pendingBox = (kind) => {
    const rows = pendingOf(kind);
    if (!rows.length) return null;
    return (
      <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: '1px dashed', borderColor: 'warning.light', bgcolor: 'rgba(237,108,2,0.04)' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark' }}>
          {isPI ? 'Waiting for your approval' : 'Your entries'}
        </Typography>
        {rows.map((c) => (
          <Box key={c.id} display="flex" alignItems="center" gap={1} py={0.5} flexWrap="wrap">
            <Box flex={1} minWidth={200}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[isPI && c.submitted_by_name && `from ${c.submitted_by_name}`, c.year,
                  !isPI && (c.status === 'rejected' ? 'not approved' : 'waiting for the PI')].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            {isPI ? (
              <>
                <Button size="small" variant="contained" color="success" disableElevation onClick={() => reviewContribution(c, 'approve')}>Approve</Button>
                <Button size="small" color="inherit" onClick={() => reviewContribution(c, 'reject')}>Reject</Button>
              </>
            ) : <ConfirmIconDelete label="Withdraw" onConfirm={() => removeContribution(kind, c)} />}
          </Box>
        ))}
      </Box>
    );
  };

  const itemControls = (kind, item) => canEdit && (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip title="Edit"><IconButton size="small" onClick={() => setItemDialog({ kind, item })} aria-label="Edit"><EditIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
      <ConfirmIconDelete onConfirm={() => removeItem(kind, item)} />
    </Box>
  );
  const openPositions = lab.positions.filter((p) => p.is_open);
  const currentProjects = lab.projects.filter((p) => !p.end_date || new Date(p.end_date) >= new Date());
  const pastProjects = lab.projects.filter((p) => p.end_date && new Date(p.end_date) < new Date());

  const answerInvitation = async (choice) => {
    try {
      const res = await axiosService.post(`/consortium/labs/${lab.id}/invitation/${choice}/`);
      if (choice === 'accept') refreshMyLabs();          // the lab appears in the sidebar
      if (res.status === 204) { navigate('/hub/', { state: { tab: 2 } }); return; }
      saved(res.data);
      showSnackbar(choice === 'accept' ? `Welcome to ${lab.name}` : 'Invitation declined', 'success');
    } catch (err) {
      showSnackbar(apiErrorMessage(err), 'error');
    }
  };

  const makePublic = async () => {
    try {
      await axiosService.patch(`/consortium/labs/${lab.id}/`, { visibility: 'public' });
      mutate();
      showSnackbar('The page is public now', 'success');
    } catch (err) {
      showSnackbar(apiErrorMessage(err), 'error');
    }
  };

  return (
    <PreviewContext.Provider value={previewCtx}>
    <Box sx={{ bgcolor: tint(color, 0.06), minHeight: '100%', pb: 4, ...(publicMode && PUBLIC_TYPE_SCALE) }}>
      {!publicMode && lab.my_invitation && (
        <Box sx={{ bgcolor: '#1f2937', color: '#fff', px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }}>
            {lab.my_invitation.is_alumni
              ? <>You are invited to be listed among the alumni of <b>{lab.name}</b>.</>
              : <>You are invited to join <b>{lab.name}</b>{lab.my_invitation.lab_title ? ` as ${lab.my_invitation.lab_title}` : ''}.</>}
            You appear on the lab, with your profile&apos;s publications, only if you accept.
          </Typography>
          <Button size="small" variant="contained" color="success" onClick={() => answerInvitation('accept')}>Accept</Button>
          <Button size="small" variant="outlined" onClick={() => answerInvitation('decline')}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>Decline</Button>
        </Box>
      )}
      {preview && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#1f2937', color: '#fff', px: 2, py: 1.25,
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
          <VisibilityIcon sx={{ fontSize: 20, opacity: 0.85 }} />
          <Box flex={1} minWidth={240}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Preview: your public page as visitors see it</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {lab.visibility === 'public'
                ? <>Live at <Link href={publicUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#93c5fd' }}>{publicUrl.replace(/^https?:\/\//, '')}</Link>. Faded sections are hidden from visitors; use the switches to show or hide them.</>
                : 'Not public yet: visitors can’t open it. Faded sections stay hidden once it is public; use the switches to choose.'}
            </Typography>
          </Box>
          {lab.visibility !== 'public' && previewCtx?.canToggle && (
            <Button size="small" variant="contained" color="success" startIcon={<PublicIcon />} onClick={makePublic}>Make it public</Button>
          )}
          <Button size="small" variant="outlined" onClick={() => navigate(`/lab/${lab.id}/`)}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
            Back to lab page
          </Button>
        </Box>
      )}
      {/* Cover */}
      <Box sx={{ background: coverBackground(lab), minHeight: 230, display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="lg" sx={{ pt: 2, display: 'flex', alignItems: 'flex-start' }}>
          {publicMode ? <Box flex={1} /> : <BackButton position="inline" sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.25)', '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' } }} />}
          {publicMode && (
            <Tooltip title="Made with Otable">
              <Box component="a" href="https://www.otable.net" target="_blank" rel="noopener noreferrer"
                sx={{ display: 'block', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 0.85 } }}>
                <Box component="img" src={otableLogo} alt="Otable"
                  sx={{ height: 34, display: 'block',
                    // a soft bevel: light upper-left edge, dark lower-right edge
                    filter: 'drop-shadow(-1px -1px 0 rgba(255,255,255,0.55)) drop-shadow(1px 1px 0 rgba(0,0,0,0.45)) drop-shadow(0 2px 3px rgba(0,0,0,0.25))' }} />
              </Box>
            </Tooltip>
          )}
        </Container>
        <Box flex={1} />
        <Container maxWidth="lg" sx={{ pb: 3 }}>
          <Box display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
            <Box flex={1} minWidth={260} sx={{ color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.15 }}>{lab.name}</Typography>
              <Box display="flex" alignItems="center" gap={2} mt={1} flexWrap="wrap" sx={{ fontSize: publicMode ? '17px' : undefined }}>
                <Link component={publicMode ? 'span' : RouterLink} to={publicMode ? undefined : `/organization/${lab.organization}`} underline={publicMode ? 'none' : 'hover'}
                  sx={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                  <BusinessIcon sx={{ fontSize: 18 }} /> {lab.organization_detail?.name}
                </Link>
                {canEdit && !publicMode && (
                  <Tooltip title={{ members: 'Visible to lab members only', platform: 'Visible to everyone on Otable', public: 'Public page: open it' }[lab.visibility] || ''}>
                    <Chip size="small"
                      icon={lab.visibility === 'public' ? <PublicIcon /> : lab.visibility === 'members' ? <LockIcon /> : <GroupsIcon />}
                      label={{ members: 'Members only', platform: 'Otable users', public: 'Public page' }[lab.visibility]}
                      // a real link (never blocked like a scripted pop-up), opening the public page in a new tab
                      {...(lab.visibility === 'public' && { component: 'a', href: `/labs/${lab.slug || lab.id}/`, target: '_blank', rel: 'noopener noreferrer', clickable: true })}
                      sx={{ bgcolor: 'rgba(0,0,0,0.3)', color: '#fff', '& .MuiChip-icon': { color: '#fff' }, height: 24,
                        cursor: lab.visibility === 'public' ? 'pointer' : 'default', textDecoration: 'none' }} />
                  </Tooltip>
                )}
             {canEdit && (
                <Button size="medium" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => navigate(`/lab/${lab.id}/preview/`)}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.8)', bgcolor: 'rgba(0,0,0,0.18)', fontWeight: 600,
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(0,0,0,0.3)' } }}>
                  Preview public page
                </Button>
              )}

                {lab.website && (
                  <Link href={lab.website} target="_blank" rel="noopener noreferrer" underline="hover"
                    sx={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LanguageIcon sx={{ fontSize: 18 }} /> Website
                  </Link>
                )}
              </Box>
            </Box>
            {!publicMode && <Stack direction="row" spacing={1.25} alignItems="center">
              <FollowButton contentType="lab" objectId={lab.id} size="medium" variant="contained"
                sx={{ bgcolor: '#fff', color: color, fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' } }} />
              {canEdit && (
                <Button size="medium" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.8)', bgcolor: 'rgba(0,0,0,0.18)', fontWeight: 600,
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(0,0,0,0.3)' } }}>
                  Edit lab
                </Button>
              )}
       
              {(lab.is_member || isPI) && (
                <Button size="medium" variant="contained" startIcon={<ChatIcon />} onClick={() => setChatOpen(true)}
                  sx={{ bgcolor: tint('#000000', 0.35), color: '#fff', fontWeight: 600, boxShadow: 'none',
                    '&:hover': { bgcolor: tint('#000000', 0.5), boxShadow: 'none' } }}>
                  Lab chat
                </Button>
              )}
              {/* the PI calls a lab meeting: proposes times, every member votes in a poll */}
              {isPI && (
                <Button size="medium" variant="contained" startIcon={<MeetingIcon />} onClick={() => setMeetingOpen(true)}
                  sx={{ bgcolor: tint('#000000', 0.35), color: '#fff', fontWeight: 600, boxShadow: 'none',
                    '&:hover': { bgcolor: tint('#000000', 0.5), boxShadow: 'none' } }}>
                  Schedule meeting
                </Button>
              )}
              {(lab.is_member || isPI) && (
                <Button size="medium" variant="contained" startIcon={<LabCalendarIcon />}
                  onClick={() => navigate(`/events/calendar/?lab=${lab.id}`)}
                  sx={{ bgcolor: tint('#000000', 0.35), color: '#fff', fontWeight: 600, boxShadow: 'none',
                    '&:hover': { bgcolor: tint('#000000', 0.5), boxShadow: 'none' } }}>
                  Lab calendar
                </Button>
              )}
              {lab.is_member && !isPI && (
                <Button size="medium" startIcon={<LeaveIcon />} onClick={() => setConfirmLeave(true)}
                  sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' } }}>
                  Leave
                </Button>
              )}
            </Stack>}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <Box minWidth={0}>
            {shown.has('about') && (
            <Section pubKey="about" title="About" color={color} empty={canEdit ? 'Add a description of the lab’s research with “Edit lab”.' : 'No description yet.'}>
              {(lab.description || lab.keywords?.length > 0) ? (
                <>
                  {lab.description && <LabMarkdown>{lab.description}</LabMarkdown>}
                  {lab.keywords?.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.75} mt={lab.description ? 2 : 0}>
                      {lab.keywords.map((k) => <Chip key={k} label={k} size="small" sx={{ bgcolor: tint(color, 0.12), color }} />)}
                    </Box>
                  )}
                </>
              ) : null}
            </Section>
            )}

            {shown.has('projects') && (
            <Section pubKey="projects" title="Active projects" count={currentProjects.length || null} color={color}
              onAdd={canEdit ? () => setItemDialog({ kind: 'projects' }) : null} addLabel="Add project"
              empty={pastProjects.length ? null : 'No projects listed yet.'}>
              {lab.projects.length > 0 ? (
                <>
                  {currentProjects.map((p) => <ProjectRow key={p.id} p={p} color={color} controls={itemControls('projects', p)} />)}
                  {currentProjects.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No active projects at the moment.</Typography>
                  )}
                  {pastProjects.length > 0 && (
                    <Accordion disableGutters elevation={0} defaultExpanded={false}
                      sx={{ mt: 1, bgcolor: 'transparent', borderTop: '1px solid', borderColor: 'divider', '&::before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                          Completed projects <Box component="span" sx={{ fontWeight: 400 }}>{pastProjects.length}</Box>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {pastProjects.map((p) => <ProjectRow key={p.id} p={p} color={color} muted controls={itemControls('projects', p)} />)}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              ) : null}
            </Section>
            )}

            {work && (work.consortia.length > 0 || work.scope !== 'visitor') && (
            <Section title="Consortia" count={work.consortia.length || null} color={color}
              empty={work.scope === 'pi' ? 'None of the lab’s members is in a consortium yet.' : 'You are not in a consortium yet.'}>
              {work.consortia.length > 0 ? <LabConsortiaBody lab={lab} consortia={work.consortia} /> : null}
            </Section>
            )}

            {work && work.scope !== 'visitor' && (
            <Section title="Works" count={work.works.length || null} color={color}
              empty={work.scope === 'pi' ? 'No proposals or publications by the lab’s members yet.' : 'You are not writing a proposal or publication yet.'}>
              {work.works.length > 0 ? (
                <>
                  <LabWorksBody lab={lab} works={work.works} />
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                    {work.scope === 'pi' ? 'Only you see all of them; members see the works they take part in.' : 'Only the works you take part in.'}
                  </Typography>
                </>
              ) : null}
            </Section>
            )}

            {shown.has('publications') && (
            <Section pubKey="publications" title="Publications" count={lab.publications.length || null} color={color}
              onAdd={canContribute ? () => setItemDialog({ kind: 'publications' }) : null} addLabel="Add publication"
              empty={pendingOf('publications').length ? null : 'Publications appear here from the members’ profiles.'}>
              {(lab.publications.length > 0 || pendingOf('publications').length > 0) ? (
                <>
                {pendingBox('publications')}
                <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                  {(allPublications ? lab.publications : lab.publications.slice(0, PUBLICATIONS_SHOWN)).map((p) => {
                    const href = p.doi ? `https://doi.org/${p.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//i, '')}` : p.url;
                    return (
                      <Box component="li" key={p.id} sx={{ mb: 1, '&::marker': { color: 'text.secondary', fontSize: '0.8rem' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {href ? <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">{p.title}</Link> : p.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[p.authors, p.journal && <i key="j">{p.journal}</i>, p.year].filter(Boolean).reduce((acc, x, i) => (i ? [...acc, ' · ', x] : [x]), [])}
                        </Typography>
                        {isPI && (
                          <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                            <Tooltip title="Hide from the lab page">
                              <IconButton size="small" aria-label="Hide from the lab page" onClick={() => setPublicationHidden(p, true)}>
                                <VisibilityOffIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            {p.source === 'lab' && <ConfirmIconDelete label="Remove from the lab page" onConfirm={() => removeContribution('publications', p)} />}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {isPI && lab.hidden_publications_detail?.length > 0 && (
                  <Accordion disableGutters elevation={0}
                    sx={{ mt: 1, bgcolor: 'transparent', borderTop: '1px dashed', borderColor: 'divider', '&::before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                      <Typography variant="caption" color="text.secondary">
                        Hidden from the lab page ({lab.hidden_publications_detail.length}) · only you see this
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                      {lab.hidden_publications_detail.map((p) => (
                        <Box key={p.id} display="flex" alignItems="center" gap={1} py={0.5} sx={{ opacity: 0.7 }}>
                          <Typography variant="body2" sx={{ flex: 1 }} noWrap>{p.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.year}</Typography>
                          <Button size="small" onClick={() => setPublicationHidden(p, false)}>Show again</Button>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}
                {lab.publications.length > PUBLICATIONS_SHOWN && (
                  <Button size="small" onClick={() => setAllPublications(!allPublications)} endIcon={<ExpandMoreIcon sx={{ transform: allPublications ? 'rotate(180deg)' : 'none' }} />}>
                    {allPublications ? 'Show fewer' : `Show all ${lab.publications.length}`}
                  </Button>
                )}
                </>
              ) : null}
            </Section>
            )}

            {shown.has('theses') && (canContribute || lab.theses?.length > 0 || preview) && (
            <Section pubKey="theses" title="Theses" count={lab.theses?.length || null} color={color}
              onAdd={canContribute ? () => setItemDialog({ kind: 'theses' }) : null} addLabel="Add thesis"
              empty={pendingOf('theses').length ? null : 'PhD, master’s and undergraduate theses from the lab.'}>
              {(lab.theses?.length > 0 || pendingOf('theses').length > 0) ? (
                <>
                  {pendingBox('theses')}
                  {[['phd', 'PhD'], ['masters', 'Master’s'], ['undergraduate', 'Undergraduate']].map(([deg, label]) => {
                    const rows = (lab.theses || []).filter((t) => t.degree === deg);
                    if (!rows.length) return null;
                    return (
                      <Box key={deg} mb={1}>
                        <Typography variant="overline" color="text.secondary">{label}</Typography>
                        {rows.map((t) => {
                          const who = t.author_detail?.name || t.author_name;
                          return (
                            <Box key={t.id} display="flex" alignItems="flex-start" gap={1.25} py={0.75}>
                              <Avatar src={t.author_detail?.avatar_url || undefined} sx={{ width: 32, height: 32, fontSize: '0.85rem', mt: 0.25, ...labAvatarSx(lab) }}>
                                {(who || '?')[0]}
                              </Avatar>
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {t.url ? <Link href={t.url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">{t.title}</Link> : t.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {[who, t.year, t.supervisor && `supervised by ${t.supervisor}`, t.institution].filter(Boolean).join(' · ')}
                                </Typography>
                              </Box>
                              {isPI && <ConfirmIconDelete label="Remove from the lab page" onConfirm={() => removeContribution('theses', t)} />}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })}
                </>
              ) : null}
            </Section>
            )}
          </Box>

          <Box minWidth={0}>
            {!publicMode && (isPI || user?.is_staff) && (
              <Section title="Lab stats" color={color}>
                <LabStatsBody lab={lab} />
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>Only you, the PI, see this.</Typography>
              </Section>
            )}

            {shown.has('members') && (
            <Section pubKey="members" title="Members" count={lab.members.length} color={color}
              onAdd={canEdit ? () => setMemberDialog({}) : null} addLabel="Add">
              {lab.members.map((m) => (
                <PersonRow key={m.id} m={m} lab={lab} noLinks={publicMode} canEdit={canEdit} onEdit={(x) => setMemberDialog({ member: x })} onRemove={removeMember}
                  onLetter={isPI ? setLetterFor : undefined} />
              ))}
              {!publicMode && lab.invitations?.length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">Invited, not yet accepted</Typography>
                  {lab.invitations.map((m) => (
                    <Box key={m.id} display="flex" alignItems="center" gap={1} py={0.5} sx={{ opacity: 0.75 }}>
                      <Avatar src={m.person?.avatar_url || undefined} sx={{ width: 28, height: 28, fontSize: '0.8rem', ...labAvatarSx(lab) }}>{(m.display_name || '?')[0]}</Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }} noWrap>{m.display_name}</Typography>
                      <Chip size="small" label={m.status === 'declined' ? 'Declined' : 'Pending'} variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                      <ConfirmIconDelete label={`Withdraw the invitation to ${m.display_name}`} onConfirm={() => removeMember(m)} />
                    </Box>
                  ))}
                </Box>
              )}
            </Section>
            )}

            {shown.has('news') && (lab.news.length > 0 || canEdit) && (
            <Section pubKey="news" title="News" count={lab.news.length || null} color={color}
              onAdd={canEdit ? () => setItemDialog({ kind: 'news' }) : null} addLabel="Add news"
              empty="Share a defence, a paper, an award or a new member.">
              {lab.news.length > 0 ? (
                <LabNewsCarousel news={lab.news} lab={lab} color={color} initials={labInitials(lab.name)}
                  avatarSx={labAvatarSx(lab)} controls={(n) => itemControls('news', n)} />
              ) : null}
            </Section>
            )}

            {!publicMode && (
              <Section title="Relevant funding calls" color={color}>
                <LabCallsBody lab={lab} />
              </Section>
            )}

            {!publicMode && (lab.is_member || canEdit) && (
              <Section title="Followers" color={color}>
                <LabFollowersBody lab={lab} />
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>Visible to lab members only.</Typography>
              </Section>
            )}

            {shown.has('positions') && (
            <Section pubKey="positions" title="Open positions" count={openPositions.length || null} color={color}
              onAdd={canEdit ? () => setItemDialog({ kind: 'positions' }) : null} addLabel="Add"
              empty={canEdit ? 'Post PhD, postdoc or other openings here.' : 'No open positions at the moment.'}>
              {(canEdit ? lab.positions : openPositions).length > 0 ? (canEdit ? lab.positions : openPositions).map((p) => (
                <Box key={p.id} sx={{ py: 1, opacity: p.is_open ? 1 : 0.55 }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <WorkIcon sx={{ fontSize: 18, color, mt: 0.25 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {publicMode ? p.title : <Link component={RouterLink} to={`/positions/${p.id}/`} underline="hover" color="inherit">{p.title}</Link>}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[KIND_LABEL[p.kind], p.deadline && `apply by ${fmtDate(p.deadline)}`, !p.is_open && 'closed'].filter(Boolean).join(' · ')}
                      </Typography>
                      {p.description && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>{p.description}</Typography>}
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                        {publicMode && p.is_open && (
                          <Button size="small" variant="contained" disableElevation onClick={() => navigate(`/lab/${lab.id}/`)}
                            sx={{ bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.92)' }, py: 0.25 }}>
                            Apply on Otable
                          </Button>
                        )}
                        {!publicMode && p.is_open && !lab.is_member && !canEdit && (
                          lab.applied_position_ids?.includes(p.id)
                            ? <Chip size="small" label="Applied" sx={{ height: 22, bgcolor: tint(color, 0.15), color }} />
                            : <Button size="small" variant="contained" disableElevation onClick={() => setApplyFor(p)}
                                sx={{ bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.92)' }, py: 0.25 }}>Apply</Button>
                        )}
                        {canEdit && (
                          <Button size="small" sx={{ px: 0 }} onClick={() => setApplicantsFor(p)}>
                            {`${lab.application_counts?.[p.id] || 0} applicant${(lab.application_counts?.[p.id] || 0) === 1 ? '' : 's'}`}
                          </Button>
                        )}
                        {p.is_open && (p.apply_url || p.contact_email) && (
                          <Button size="small" sx={{ px: 0 }} href={p.apply_url || `mailto:${p.contact_email}`}
                            target={p.apply_url ? '_blank' : undefined} rel="noopener noreferrer">More info</Button>
                        )}
                      </Box>
                    </Box>
                    {itemControls('positions', p)}
                  </Box>
                </Box>
              )) : null}
            </Section>
            )}

            {shown.has('alumni') && (
            <Section pubKey="alumni" title="Alumni" count={lab.alumni.length || null} color={color}
              onAdd={canEdit ? () => setMemberDialog({ alumni: true }) : null} addLabel="Add"
              empty="No alumni listed yet.">
              {lab.alumni.length > 0 ? lab.alumni.map((m) => (
                <PersonRow key={m.id} m={m} lab={lab} noLinks={publicMode} alumni canEdit={canEdit} onEdit={(x) => setMemberDialog({ member: x })} onRemove={removeMember}
                  onLetter={isPI ? setLetterFor : undefined} />
              )) : null}
            </Section>
            )}
          </Box>
        </Box>

        {publicMode && (
          <Box component="footer" sx={{ mt: 5, pt: 2.5, pb: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Lab page on{' '}
              <Link href="https://www.otable.net" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>otable.net</Link>
              {' '}· by Helixome Life Sciences and Information Technologies Ltd.
            </Typography>
          </Box>
        )}

        {!publicMode && <Box textAlign="center" mt={3}>
          <Link component={RouterLink} to="/hub/" state={{ tab: 2 }} underline="hover"
            sx={{ color: 'text.disabled', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <SchoolIcon sx={{ fontSize: 14 }} /> Lab page on Otable · discover more labs
          </Link>
        </Box>}
      </Container>

      <LabEditDialog open={editOpen} onClose={() => setEditOpen(false)} lab={lab} isPI={isPI || user?.is_staff}
        onSaved={saved} onDeleted={() => { showSnackbar('Lab deleted', 'success'); navigate('/hub/', { state: { tab: 2 } }); }} />
      <Dialog open={confirmLeave} onClose={() => !leaving && setConfirmLeave(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Leave {lab.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14.5, mb: 1.5 }}>
            You will no longer be listed {myMembership?.is_alumni ? 'among its alumni' : 'as a member'} on the lab page.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 14, color: 'text.secondary', '& li': { mb: 0.5 } }}>
            <li>You lose access to the lab chat{lab.visibility === 'members' ? ' and to this members-only page' : ''}.</li>
            {myMembership?.can_edit && <li>You can no longer edit the lab page.</li>}
            {!myMembership?.is_alumni && <li>The publications from your profile stop showing on the lab page.</li>}
            <li>To come back, the PI has to invite you again.</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)} disabled={leaving}>Stay</Button>
          <Button color="error" variant="contained" onClick={leave} disabled={leaving}
            startIcon={leaving ? <CircularProgress size={14} color="inherit" /> : null}>
            Leave the lab
          </Button>
        </DialogActions>
      </Dialog>
      {letterFor && <ReferenceLetterDialog open lab={lab} member={letterFor} onClose={() => setLetterFor(null)} />}
      <MemberDialog open={Boolean(memberDialog)} onClose={() => setMemberDialog(null)} lab={lab}
        member={memberDialog?.member} alumni={memberDialog?.alumni} isPI={isPI || user?.is_staff} onSaved={saved} />
      <ItemDialog open={Boolean(itemDialog)} onClose={() => setItemDialog(null)} lab={lab} isPI={isPI || user?.is_staff}
        kind={itemDialog?.kind} item={itemDialog?.item} onSaved={saved} />
      <ApplyDialog open={Boolean(applyFor)} onClose={() => setApplyFor(null)} lab={lab} position={applyFor} onApplied={saved} />
      <ApplicantsDialog open={Boolean(applicantsFor)} onClose={() => { setApplicantsFor(null); mutate(); }} lab={lab} position={applicantsFor} />
      {(lab.is_member || isPI) && <LabChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} lab={lab} />}
      {/* a lab meeting: the PI proposes times and every current member on Otable votes in a poll (creating the
          poll notifies each of them, by notification and email) */}
      {isPI && (
        <LabMeetingDialog lab={lab} open={meetingOpen} onClose={() => setMeetingOpen(false)}
          onDone={() => showSnackbar('Meeting poll sent: the lab members are notified to vote', 'success')} />
      )}
    </Box>
    </PreviewContext.Provider>
  );
}
